import { defineStore } from 'pinia'
import { notificationSettingsApi } from '../services/notificationSettingsApi'
import { isCriticalType } from '../composables/useNotificationType'
import type {
  INotificationSettingSync,
  INotificationSettingsCategory,
  INotificationSettingsChannel,
  TNotificationChannel,
} from '../types/notificationSettings.types'
import type { TNotificationMessageType } from '../types/notification.types'

/**
 * Стор настроек уведомлений (матрица канал × тип, без звука).
 *
 * Структура preset — список каналов и древо «категории → типы → каналы (галочки)».
 * Хранится как пришло; правки делаются in-place через setSetting, сохранение
 * собирает плоский массив для PUT /sync/.
 *
 * Валидация: для критичных типов (adverse_event/ticket/responsibility_entry, см.
 * isCriticalType) запрещено отключать **все** каналы — должен быть включён минимум один.
 * При попытке выключить последний бек-канал toggle блокируется в UI (см. геттер
 * `canDisable`).
 */
export const useNotificationSettingsStore = defineStore('notificationSettingsStore', {
  state: () => ({
    channels: [] as INotificationSettingsChannel[],
    categories: [] as INotificationSettingsCategory[],
    isLoading: false,
    isSaving: false,
    isDirty: false,
  }),

  getters: {
    /**
     * Плоский список всех типов из всех категорий — нужен для проверки критичности
     * и сборки массива для /sync/.
     */
    allTypes(state) {
      return state.categories.flatMap(c => c.types)
    },

    /**
     * Признак — есть ли среди критичных типов такой, у которого все каналы выключены.
     * Используется для дизейбла кнопки «Сохранить».
     */
    hasInvalidCritical(): boolean {
      return this.allTypes
        .filter(t => isCriticalType(t.type))
        .some(t => Object.values(t.channels).every(v => v !== true))
    },
  },

  actions: {
    /** Загрузить актуальный preset с бека. */
    async fetchSettings(): Promise<void> {
      this.isLoading = true
      try {
        const { data } = await notificationSettingsApi.getPreset()
        this.channels = data.settings?.channels ?? []
        this.categories = data.settings?.categories ?? []
        this.isDirty = false
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Проверка — можно ли выключить чекбокс (channel, type).
     * Возвращает false если это критичный тип и попытка снять последний активный канал.
     * Включить чекбокс — всегда можно.
     */
    canDisable(channel: TNotificationChannel, type: TNotificationMessageType): boolean {
      if (!isCriticalType(type)) return true
      const target = this.allTypes.find(t => t.type === type)
      if (!target) return true
      const activeCount = Object.values(target.channels).filter(v => v === true).length

      return activeCount > 1 || target.channels[channel] !== true
    },

    /** Установить значение чекбокса (для v-model VCheckbox в матрице). */
    setSetting(channel: TNotificationChannel, type: TNotificationMessageType, value: boolean): void {
      const target = this.allTypes.find(t => t.type === type)
      if (!target) return
      if (!(channel in target.channels)) return
      if (target.channels[channel] === value) return

      if (value === false && !this.canDisable(channel, type)) return

      target.channels[channel] = value
      this.isDirty = true
    },

    /** Отправить текущий state на бек через /sync/. */
    async saveSettings(): Promise<void> {
      if (this.hasInvalidCritical) return
      this.isSaving = true
      try {
        const pairs: INotificationSettingSync[] = []
        this.allTypes.forEach(t => {
          Object.entries(t.channels).forEach(([channel, isEnabled]) => {
            pairs.push({
              channel,
              message_type: t.type,
              is_enabled: Boolean(isEnabled),
            })
          })
        })
        const { data } = await notificationSettingsApi.syncSettings(pairs)
        this.channels = data.settings?.channels ?? this.channels
        this.categories = data.settings?.categories ?? this.categories
        this.isDirty = false
      } finally {
        this.isSaving = false
      }
    },
  },
})
